import PageBanner from '../components/PageBanner';
import ProductsList from '../components/products-list';
import { useLocation } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ItemsList from '@features/shop/components/products-list/ItemsList';
import FreeGiftCta from '@components/tailwind/FreeGiftCta';
import PageDesignGridElement from '@components/tailwind/PageDesignGridElement';
import { usePageDesignHeaderMaxWidths } from '../hooks/usePageDesignHeaderMaxWidths';
import useVariation from '@hooks/useVariation';
import SearchText from '../components/SearchText';
import classNames from 'classnames';
import usePageDesign from '../hooks/usePageDesign';
import BrowseBy from '@features/shop/components/BrowseBy';
import ProductFeed from '../components/products-list/ProductFeed';
import Element from '@features/cms/components/Element';

const reorderGrid = (grid) => {
  const index = grid.findIndex((g) => g.name === 'PLACEHOLDER:CATEGORYMODULE');
  if (index >= 0) {
    const module = grid[index];
    const copy = [...grid];
    copy.splice(index, 1);
    copy.splice(0, 0, module);
    return copy;
  } else {
    return grid;
  }
};

const PageDesignPage = ({ pageContent, pageDesign }) => {
  const { header } = pageContent; // TODO make this ajax driven like design

  const location = useLocation();
  const maxWidths = usePageDesignHeaderMaxWidths({
    header: pageDesign?.pageDesign?.header,
  });

  const debug = useVariation('debug'); // !!(new URLSearchParams(location.search).get('debug'));

  if (!pageDesign) return null;

  const { pageDesign: pd, items, filterOptions, rootHierarchy, searchTerm, components } = pageDesign;
  const {
    breadcrumbs,
    header: pgHeader,
    itemGridElements,
    grid: rawGrid,
    flags,
    siteId,
    elements,
    header: pdHeader,
  } = pd || {};
  const { showNav, useVariantUrl } = flags || {};

  const renderedHeaderStyles = useMemo(
    () =>
      pgHeader &&
      maxWidths.map(({ mediaQuery, maxWidth }) => {
        if (!mediaQuery && !maxWidth) return;
        if (!mediaQuery) {
          return `
          .header {
            max-width: ${maxWidth}px;
            margin-left: auto;
            margin-right: auto;
          }`;
        }

        return `
        @media ${mediaQuery} {
          .header {
            max-width: ${maxWidth}px;
            margin-left: auto;
            margin-right: auto;
          }`;
      }),
    [maxWidths]
  );

  const isHomePage = location.pathname === '/';
  const isHardCoolersVariant = useVariation('Hard Coolers');
  const isSoftCoolersVariant = useVariation('Soft Coolers');

  const shouldHideHeader =
    (isHardCoolersVariant && location.pathname === '/shop/coolers/hard-sided') ||
    (isSoftCoolersVariant && location.pathname === '/shop/coolers/soft-sided');

  const isTestActive = useVariation('Homepage Module Order');

  const grid = isHomePage && isTestActive ? reorderGrid(rawGrid) : rawGrid;

  return (
    <section>
      <Section position='before_header' {...{ components, elements, debug }} />

      {pgHeader && !shouldHideHeader && (
        <>
          {renderedHeaderStyles && <style>{renderedHeaderStyles}</style>}
          <PageDesignGridElement
            className={classNames('header', { 'tw-pb-10': isHomePage })}
            element={pgHeader}
          />
        </>
      )}

      <PageBanner
        image={!shouldHideHeader ? header?.image : null}
        title={header?.title || pgHeader?.title}
        video={!shouldHideHeader ? header?.video : null}
        crumbs={
          pdHeader?.showBreadcrumbs
            ? (breadcrumbs || []).map((crumb) => ({ ...crumb, link: undefined, url: crumb.link }))
            : []
        }
        text={pgHeader?.bodyText}
        titlePos={header?.titlePos || pgHeader?.titlePos}
      />

      <FreeGiftCta />

      <Section position='after_header' {...{ components, elements, debug }} />

      <Section position='before_grid' {...{ components, elements, debug }} />

      {grid?.length > 0 && <ItemsList grid={grid} />}

      <Section position='after_grid' {...{ components, elements, debug }} />

      <Section position='before_items' {...{ components, elements, debug }} />

      <SearchText itemsCount={items.length} searchTerm={searchTerm} />

      {items?.length > 0 && (
        <ProductsList
          siteId={siteId}
          showNav={showNav}
          items={items}
          itemGridElements={itemGridElements}
          filterOptions={filterOptions || {}}
          productCardProps={{ buttonVariant: 'tertiary' }}
          rootHierarchy={rootHierarchy}
          useVariantUrl={useVariantUrl}
          hideItemsCount={Boolean(searchTerm)}
        />
      )}

      <Section position='after_items' {...{ components, elements, debug }} />
    </section>
  );
};

const Section = ({ position, components, elements, debug = false }) => {
  if (debug) {
    return (
      <div className='tw-relative -tw-mx-2 tw-my-6 tw-min-h-[40px] tw-rounded tw-border-2 tw-border-dotted tw-border-neutrals-light-grey tw-py-4'>
        <div className='tw-absolute -tw-top-3 tw-w-full tw-text-center'>
          <div className='tw-mx-auto tw-inline-block tw-w-[12rem] tw-border tw-border-neutrals-light-grey tw-bg-white tw-p-1 tw-text-center tw-text-xs'>
            {position}:BEGIN
          </div>
        </div>
        <div className='tw-absolute -tw-bottom-3 tw-w-full tw-text-center'>
          <div className='tw-mx-auto tw-inline-block tw-w-[12rem] tw-border tw-border-neutrals-light-grey tw-bg-white tw-p-1 tw-text-center tw-text-xs'>
            {position}:END
          </div>
        </div>
        <SectionImpl {...{ position, components, elements, debug }} />
      </div>
    );
  } else {
    return <SectionImpl {...{ position, components, elements, debug }} />;
  }
};

const SectionImpl = ({ position, components: allComponents, elements: allElements, debug = false }) => {
  const components = useMemo(() => {
    return allComponents.reduce((a, c) => {
      if (c.position === position) {
        a.push(c);
      }
      return a;
    }, []);
  }, [allComponents]);

  const elements = useMemo(() => {
    return (
      allElements &&
      allElements.reduce((a, c) => {
        if ((c.position ?? 'after_item') === position) {
          a.push(c);
        }
        return a;
      }, [])
    );
  }, [allElements]);

  if (!components?.length && !elements?.length) return null;

  return (
    <div className='tw-relative tw-mx-auto tw-w-full tw-max-w-[1480px] tw-px-4'>
      {components.map((component, i) => (
        <PageComponent key={'component.' + i} {...{ position, component, debug }} />
      ))}
      {elements.map((element, i) => (
        <Element key={'element.' + i} {...element} />
      ))}
    </div>
  );
};

const PageComponent = ({ component, debug = false }) => {
  if (debug) {
    return (
      <div className='tw-relative -tw-mx-2 tw-my-2 tw-rounded tw-border-2 tw-border-dotted tw-border-neutrals-light-grey tw-p-2'>
        <div className='tw-absolute -tw-top-3 tw-left-4 tw-border tw-border-neutrals-light-grey tw-bg-white tw-p-1 tw-text-xs'>
          {component.componentType}:{component.meta.slug}
        </div>
        <PageComponentImpl {...{ component, debug }} />
      </div>
    );
  } else {
    return <PageComponentImpl {...{ component }} />;
  }
};
const PageComponentImpl = ({ component, debug = false }) => {
  if (component.componentType === 'browse') {
    return (
      <BrowseBy
        title={component.meta.title}
        items={component.grid.map((g) => ({
          title: g.name,
          url: g.actionLink.url,
          imageUrl: g.image.sources[0]?.webImage?.url,
        }))}
      />
    );
  } else if (component.componentType === 'faq' || component.componentType === 'content') {
    return (
      <>
        {component.grid.map((element, i) => (
          <PageDesignGridElement key={'grid.' + i} element={element} index={i} useDimensionFix rounded />
        ))}
      </>
    );
  } else if (component.componentType === 'header') {
    return <PageDesignGridElement className={classNames('header')} element={component.header} />;
  } else if (component.componentType === 'grid') {
    return <ItemsList grid={component.grid} />;
  } else if (component.componentType === 'products') {
    return (
      <ProductFeed
        siteId={component.siteId}
        items={component.cards}
        filterOptions={component.filterOptions || {}}
        productCardProps={{ buttonVariant: 'tertiary' }}
        useVariantUrl={component.flags.useVariantUrl}
        title={component.meta.title}
      />
    );
  } else {
    return debug ? <div>UNHANDLED COMPONENT TYPE</div> : null;
  }
};

export default PageDesignPage;
